/**
 * Org-wide dial / connecting / ringtone / chime config (Provider Owner / L4).
 * Stored on the L4 user's settingsJson as `dialToneOrgConfig`.
 */

export const DIAL_TONE_ORG_CONFIG_KEY = 'dialToneOrgConfig' as const

/** Max duration for voice/video dial-tone uploads (ms). */
export const DIAL_TONE_MAX_DURATION_MS = 60_000

/** How many times a dial tone plays before the unanswered call disconnects. */
export const DIAL_TONE_REPEAT_COUNT = 3

/** Soft cap for connecting-tone uploads (ms). Longer files still loop until connected. */
export const CONNECTING_TONE_MAX_DURATION_MS = 120_000

/** Max custom + seeded options per ringtone / message-chime catalog. */
export const SOUND_CATALOG_MAX_SLOTS = 8

export type DialToneSlot = {
  id: string
  /** Persistent S3 attachment URL, or a bundled `/sounds/…` path for builtins. */
  attachmentUrl: string
  /**
   * S3 key, or `builtin:<id>` for seeded bundled defaults that do not need upload.
   */
  attachmentKey: string
  label: string
  /** Measured client-side at upload time (0 for builtins until previewed). */
  durationMs: number
  contentType: string
  createdAt: string
}

export type DialToneSectionMode = 'random' | 'single'

export type DialToneSection = {
  /** When true (default) and ≥2 slots, pick a random slot per outbound call. */
  randomEnabled: boolean
  /** Used when randomEnabled is false (or only one slot). */
  selectedId: string | null
  slots: DialToneSlot[]
}

export type ConnectingToneConfig = {
  slot: DialToneSlot | null
}

/** User-selectable ringtone / message-chime options curated by the org owner. */
export type SoundCatalog = {
  slots: DialToneSlot[]
}

export type DialToneOrgConfig = {
  voiceDial: DialToneSection
  videoDial: DialToneSection
  connecting: ConnectingToneConfig
  /** Options users may pick as their personal ringtone (Display settings). */
  ringtoneCatalog: SoundCatalog
  /** Options users may pick as their personal message chime. */
  chimeCatalog: SoundCatalog
  updatedAt: string
  updatedBy: string
}

export function emptyDialToneSection(): DialToneSection {
  return { randomEnabled: true, selectedId: null, slots: [] }
}

export function isBuiltinSoundSlot(slot: Pick<DialToneSlot, 'attachmentKey'>): boolean {
  return slot.attachmentKey.startsWith('builtin:')
}

export function builtinSoundSlot(
  builtinId: string,
  label: string,
  publicPath: string,
): DialToneSlot {
  return {
    id: `builtin-${builtinId}`,
    attachmentUrl: publicPath,
    attachmentKey: `builtin:${builtinId}`,
    label,
    durationMs: 0,
    contentType: 'audio/wav',
    createdAt: '2026-01-01T00:00:00.000Z',
  }
}

/** Seeded defaults shown in Tone & Chime Settings before the owner uploads more. */
export function defaultRingtoneCatalog(): SoundCatalog {
  return {
    slots: [
      builtinSoundSlot(
        'gentle-ascent',
        'The Gentle Ascent',
        '/sounds/ringtone-gentle-ascent.wav',
      ),
    ],
  }
}

export function defaultChimeCatalog(): SoundCatalog {
  return {
    slots: [
      builtinSoundSlot('ascend-pop', 'Ascend Pop', '/sounds/chime-ascend-pop.wav'),
    ],
  }
}

export function emptyDialToneOrgConfig(updatedBy = '', updatedAt = new Date().toISOString()): DialToneOrgConfig {
  return {
    voiceDial: emptyDialToneSection(),
    videoDial: emptyDialToneSection(),
    connecting: { slot: null },
    ringtoneCatalog: defaultRingtoneCatalog(),
    chimeCatalog: defaultChimeCatalog(),
    updatedAt,
    updatedBy,
  }
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function normalizeSlot(raw: unknown): DialToneSlot | null {
  if (!isRecord(raw)) return null
  const id = typeof raw.id === 'string' ? raw.id : ''
  const attachmentUrl = typeof raw.attachmentUrl === 'string' ? raw.attachmentUrl : ''
  const attachmentKey = typeof raw.attachmentKey === 'string' ? raw.attachmentKey : ''
  if (!id || !attachmentUrl) return null
  return {
    id,
    attachmentUrl,
    attachmentKey,
    label: typeof raw.label === 'string' && raw.label.trim() ? raw.label.trim() : 'Custom tone',
    durationMs: typeof raw.durationMs === 'number' && raw.durationMs > 0 ? raw.durationMs : 0,
    contentType: typeof raw.contentType === 'string' ? raw.contentType : 'audio/mpeg',
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : new Date().toISOString(),
  }
}

function normalizeSection(raw: unknown): DialToneSection {
  if (!isRecord(raw)) return emptyDialToneSection()
  const slots = Array.isArray(raw.slots)
    ? raw.slots.map(normalizeSlot).filter((s): s is DialToneSlot => Boolean(s)).slice(0, 3)
    : []
  let selectedId = typeof raw.selectedId === 'string' ? raw.selectedId : null
  if (selectedId && !slots.some((s) => s.id === selectedId)) selectedId = slots[0]?.id ?? null
  if (!selectedId && slots.length === 1) selectedId = slots[0].id
  return {
    randomEnabled: raw.randomEnabled !== false,
    selectedId,
    slots,
  }
}

function normalizeCatalog(raw: unknown, max = SOUND_CATALOG_MAX_SLOTS): SoundCatalog {
  if (!isRecord(raw) || !Array.isArray(raw.slots)) return { slots: [] }
  const slots = raw.slots
    .map(normalizeSlot)
    .filter((s): s is DialToneSlot => Boolean(s))
    .slice(0, max)
  return { slots }
}

export function normalizeDialToneOrgConfig(raw: unknown): DialToneOrgConfig | null {
  if (!isRecord(raw)) return null
  const voiceDial = normalizeSection(raw.voiceDial)
  const videoDial = normalizeSection(raw.videoDial)
  const connectingRaw = isRecord(raw.connecting) ? raw.connecting : {}
  const connectingSlot = normalizeSlot(connectingRaw.slot)
  const ringtoneCatalog = normalizeCatalog(raw.ringtoneCatalog)
  const chimeCatalog = normalizeCatalog(raw.chimeCatalog)
  const hasAny =
    voiceDial.slots.length > 0 ||
    videoDial.slots.length > 0 ||
    Boolean(connectingSlot) ||
    ringtoneCatalog.slots.length > 0 ||
    chimeCatalog.slots.length > 0
  if (!hasAny && !raw.updatedAt) return null
  return {
    voiceDial,
    videoDial,
    connecting: { slot: connectingSlot },
    ringtoneCatalog,
    chimeCatalog,
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : new Date().toISOString(),
    updatedBy: typeof raw.updatedBy === 'string' ? raw.updatedBy : '',
  }
}

/**
 * Stable 0..1 value from a string so caller + callee with the same callId
 * pick the same random dial tone without signaling the slot.
 */
export function unitFromSeed(seed: string): number {
  let h = 2166136261
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  // Exclude 1.0 so Math.floor(unit * n) stays in range.
  return (h >>> 0) / 0x100000000
}

/** Pick which dial slot to play (or null → built-in default). */
export function pickDialToneSlot(
  section: DialToneSection,
  /**
   * RNG, or a seed string (typically callId) so both call parties resolve the
   * same slot in random mode.
   */
  rngOrSeed: (() => number) | string = Math.random,
): DialToneSlot | null {
  if (section.slots.length === 0) return null
  if (!section.randomEnabled || section.slots.length === 1) {
    const selected =
      section.slots.find((s) => s.id === section.selectedId) ?? section.slots[0]
    return selected
  }
  const unit = typeof rngOrSeed === 'string' ? unitFromSeed(rngOrSeed) : rngOrSeed()
  const idx = Math.floor(unit * section.slots.length)
  return section.slots[Math.min(idx, section.slots.length - 1)]
}

/** Unanswered-call timeout derived from dial tone length × repeat count. */
export function dialToneHangupTimeoutMs(slot: DialToneSlot | null, fallbackMs: number): number {
  if (!slot || !(slot.durationMs > 0)) return fallbackMs
  const plays = DIAL_TONE_REPEAT_COUNT * slot.durationMs
  // Small buffer so the 3rd play can finish before hangup.
  return Math.min(plays + 1_500, DIAL_TONE_REPEAT_COUNT * DIAL_TONE_MAX_DURATION_MS + 2_000)
}

/** Ensure editor always shows at least the seeded default catalogs. */
export function withSeededSoundCatalogs(config: DialToneOrgConfig): DialToneOrgConfig {
  return {
    ...config,
    ringtoneCatalog:
      config.ringtoneCatalog.slots.length > 0 ? config.ringtoneCatalog : defaultRingtoneCatalog(),
    chimeCatalog: config.chimeCatalog.slots.length > 0 ? config.chimeCatalog : defaultChimeCatalog(),
  }
}
